import Vue from "vue";
const CommonRepository = RepositoryFactory.get("common");
import { RepositoryFactory } from "src/api/repositories/RepositoryFactory";
import { PAGE_CLONE, PAGE_NEW, POST_CLONE, POST_NEW, ensurePermission } from "./authoringToolsConfig";

export default {
    namespaced: true,
    state: {
        addableEditedSection: null, // tracks the section that had its add section below clicked
        elementMenuDisplay: false, // flag to show/hide list of elements that can be inserted
        elementMenuFocusedCategory: "", // holds the current category that is currently in view in the elements list
        codeEditorsWindowDisplay: false,
        codeEditorsWindowActiveTab: "pageHeadIncludesLoggedIn",
        iconMenuDisplay: false,
        iconMenuNodeState: {}, // currently edited node state that's used by the icon library menu
        imageLibraryActive: false, //display of image library
        imageEditorActive: false, //display of image editor
        imageEditorFile: null, //display of image editor
        videoLibraryActive: false, //display of video library
        sectionMenuDisplay: false,
        scaledPreviewPageMode: false,
        globalStylingMenuDisplay: false,
        newPageModalDisplay: false, //modal window for page creation
        newBlogPostModalDisplay: false, //modal window for blog post creation
        cloneBlogPostModalDisplay: false, //modal window for cloning blog post
        clonePageModalDisplay: false, //modal window for page cloning
        nodeStateToInsertAfter: null, // tracks the node that had its add (+) button clicked
        nodeStateToAppendIn: null, // tracks the node that had its add (+) button clicked
        pageSettingsMenuDisplay: false,
        siteSettingsMenuDisplay: false,
        pageCatalogueWindowDisplay: false,
        pageCatalogueWindowSelectedGroup: null,
        pageCatalogueSelectedTemplate: "",
        postCatalogueWindowDisplay: false,
        postCatalogueWindowSelectedGroup: "",
        linksManagerDisplay: false,
        linksManagerNode: null,
        linksManagerCourseContents: {},
        linksManagerOptions: {}, //holds options that enables disabled certain features of links manager
        buttonTypeModalDisplay: false,
        buttonTypeModalNodeState: {},
        inputTypeModalDisplay: false,
        inputTypeModalNodeState: {},
        screenTypeModalDisplay: false,
        screenTypeModalNodeState: {},
        lottieAnimationModalDisplay: false,
        lottieAnimationModalNodeState: {},
    },
    getters: {},
    actions: {
        setPageCatalogueSelectedTemplate({ commit }, payload) {
            commit("setPageCatalogueSelectedTemplate", payload);
        },
        getLinksManagerData({ commit, dispatch }) {
            return CommonRepository.getLinksManagerData().then((result) => {
                if (result.data.success) {
                    //blog posts have all the data needed to be used in pageseditor
                    if (result.data.blogPosts) {
                        dispatch("blog/setBlogPosts", result.data.blogPosts, {
                            root: true,
                        });
                    }

                    // courses on the other hand only serve their contents without ebook/videos/objects etc.... so we cant merge them with partial data
                    if (result.data.courses) {
                        commit("setLinksManagerCourses", result.data.courses);
                    }
                }

                return result.data;
            });
        },
        setCodeEditorsWindowDisplay({ commit, dispatch }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setCodeEditorsWindowDisplay", payload);
        },
        setCodeEditorsWindowActiveTab({ commit }, payload) {
            commit("setCodeEditorsWindowActiveTab", payload);
        },
        setNewPageModalDisplay: ensurePermission(PAGE_NEW, ({ commit }, flag) => {
            commit("setNewPageModalDisplay", flag);
        }),
        setNewBlogPostModalDisplay: ensurePermission(POST_NEW,({ commit }, flag) => {
            commit("setNewBlogPostModalDisplay", flag);
        }),
        setCloneBlogPostModalDisplay: ensurePermission(POST_CLONE,({ commit }, flag) => {
            commit("setCloneBlogPostModalDisplay", flag);
        }),
        setClonePageModalDisplay: ensurePermission(PAGE_CLONE,({ commit }, flag) => {
            commit("setClonePageModalDisplay", flag);
        }),
        setPageCatalogueWindowDisplay({ commit }, payload) {
            commit("setPageCatalogueWindowDisplay", payload);
        },
        setPostCatalogueWindowDisplay({ commit }, payload) {
            commit("setPostCatalogueWindowDisplay", payload);
        },
        setSectionMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setSectionMenuDisplay", payload);
        },
        setElementMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setElementMenuDisplay", payload);
        },
        setElementMenuDisplayedCategory({ commit }, payload) {
            commit("setElementMenuDisplayedCategory", payload);
        },
        setGlobalStylingMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");

            if (payload) {
                dispatch(
                    "UiInteractions/setElementStatus",
                    {
                        hoveredElement: null,
                        hoveredSection: null,
                        selectedElement: null,
                        selectedSection: null,
                    },
                    { root: true }
                );
                dispatch("setScaledPreviewPageMode", true);
            } else {
                dispatch("setScaledPreviewPageMode", false);
            }

            commit("setGlobalStylingMenuDisplay", payload);
        },
        setScaledPreviewPageMode({ dispatch, commit }, payload) {
            if (payload) {
                dispatch("UiInteractions/setActiveDevicePreview", "desktop", {
                    root: true,
                });
            }

            commit("setScaledPreviewPageMode", payload);
        },
        setIconMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setIconMenuDisplay", payload);
        },
        setIconMenuNodeState({ commit }, payload) {
            commit("setIconMenuNodeState", payload);
        },
        setPageSettingsMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setPageSettingsMenuDisplay", payload);
            dispatch("setScaledPreviewPageMode", Boolean(payload));
        },
        setSiteSettingsMenuDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setSiteSettingsMenuDisplay", payload);
        },
        resetWindowsDisplay({ commit, dispatch }) {
            commit("resetWindowsDisplay");
        },
        setNodeStateToInsertAfter({ commit }, payload) {
            commit("setNodeStateToInsertAfter", payload);
        },
        setNodeStateToAppendIn({ commit }, payload) {
            commit("setNodeStateToAppendIn", payload);
        },
        setEditedAddableSection({ commit }, payload) {
            commit("setEditedAddableSection", payload);
        },
        setImageLibraryStatus({ commit }, payload) {
            commit("setImageLibraryStatus", payload);
        },
        setImageEditorStatus({ commit }, payload) {
            commit("setImageEditorStatus", payload);
        },
        setImageEditorFile({ commit }, payload) {
            commit("setImageEditorFile", payload);
        },
        setVideoLibraryStatus({ commit }, payload) {
            commit("setVideoLibraryStatus", payload);
        },
        setLinksManagerDisplay({ dispatch, commit }, payload) {
            commit("setLinksManagerDisplay", payload);

            if (!payload) {
                dispatch("setLinksManagerOptions", {});
            }
        },
        setLinksManagerOptions({ dispatch, commit }, payload) {
            commit("setLinksManagerOptions", payload);
        },
        setLinksManagerNode({ commit }, payload) {
            commit("setLinksManagerNode", payload);
        },
        setButtonTypeModalDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setButtonTypeModalDisplay", payload);
        },
        setButtonTypeModalNodeState({ commit }, payload) {
            commit("setButtonTypeModalNodeState", payload);
        },
        setInputTypeModalDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setInputTypeModalDisplay", payload);
        },
        setInputTypeModalNodeState({ commit }, payload) {
            commit("setInputTypeModalNodeState", payload);
        },
        setScreenTypeModalDisplay({ dispatch, commit }, payload) {
            dispatch("resetWindowsDisplay");
            commit("setScreenTypeModalDisplay", payload);
        },
        setScreenTypeModalNodeState({ commit }, payload) {
            commit("setScreenTypeModalNodeState", payload);
        },
        setLottieAnimationModalDisplay ( { dispatch , commit} , payload) {
            dispatch('resetWindowsDisplay');
            commit('setLottieAnimationModalDisplay',payload);
        },
        setLottieAnimationModalNodeState ( {commit},payload) {
            commit('setLottieAnimationModalNodeState',payload);
        },
    },
    mutations: {
        setPageCatalogueSelectedTemplate(state, templateId) {
            state.pageCatalogueSelectedTemplate = templateId;
        },
        setLinksManagerCourses(state, courses) {
            state.linksManagerCourseContents = courses;
        },
        setScaledPreviewPageMode(state, flag) {
            state.scaledPreviewPageMode = flag;
        },
        setPostCatalogueWindowDisplay(state, flag) {
            state.postCatalogueWindowDisplay = flag;
        },
        setPageCatalogueWindowDisplay(state, flag) {
            state.pageCatalogueWindowDisplay = flag;
        },
        setPageCatalogueWindowSelectedGroup(state, group) {
            state.pageCatalogueWindowSelectedGroup = group;
        },
        setPostCatalogueWindowSelectedGroup(state, group) {
            state.postCatalogueWindowSelectedGroup = group;
        },
        setCodeEditorsWindowActiveTab(state, tab) {
            state.codeEditorsWindowActiveTab = tab;
        },
        setCodeEditorsWindowDisplay(state, flag) {
            state.codeEditorsWindowDisplay = flag;
        },
        setElementMenuDisplayedCategory(state, category) {
            state.elementMenuFocusedCategory = category;
        },
        setNewPageModalDisplay(state, flag) {
            state.newPageModalDisplay = flag;
        },
        setNewBlogPostModalDisplay(state, flag) {
            state.newBlogPostModalDisplay = flag;
        },
        setCloneBlogPostModalDisplay(state, flag) {
            state.cloneBlogPostModalDisplay = flag;
        },
        setClonePageModalDisplay(state, flag) {
            state.clonePageModalDisplay = flag;
        },
        setLinksManagerOptions(state, options) {
            if (options) {
                for (let key in options) {
                    if (!options.hasOwnProperty(key)) {
                        continue;
                    }

                    if (typeof state.linksManagerOptions[key] === "undefined") {
                        Vue.set(state.linksManagerOptions, key, options[key]);
                    } else {
                        state.linksManagerOptions[key] = options[key];
                    }
                }
            } else {
                state.linksManagerOptions = {};
            }
        },
        setLinksManagerDisplay(state, display) {
            state.linksManagerDisplay = display;
        },
        setLinksManagerNode(state, node) {
            state.linksManagerNode = node;
        },
        setSectionMenuDisplay(state, flag) {
            state.sectionMenuDisplay = flag;
        },
        setElementMenuDisplay(state, flag) {
            state.elementMenuDisplay = flag;
        },
        setGlobalStylingMenuDisplay(state, flag) {
            state.globalStylingMenuDisplay = flag;
        },
        setPageSettingsMenuDisplay(state, flag) {
            state.pageSettingsMenuDisplay = flag;
        },
        setSiteSettingsMenuDisplay(state, flag) {
            state.siteSettingsMenuDisplay = flag;
        },
        setIconMenuDisplay(state, flag) {
            state.iconMenuDisplay = flag;
        },
        setIconMenuNodeState(state, data) {
            state.iconMenuNodeState = data;
        },
        resetWindowsDisplay(state) {
            state.elementMenuDisplay = false;
            state.sectionMenuDisplay = false;
            state.newPageModalDisplay = false;
            state.clonePageModalDisplay = false;
            state.pageCatalogueWindowDisplay = false;
            state.iconMenuDisplay = false;
            state.imageLibraryActive = false;
            state.imageEditorActive = false;
            state.videoLibraryActive = false;
            state.linksManagerDisplay = false;
            state.inputTypeModalDisplay = false;
            state.screenTypeModalDisplay = false;
            state.globalStylingMenuDisplay = false;
            state.pageSettingsMenuDisplay = false;
            state.siteSettingsMenuDisplay = false;
            state.codeEditorsWindowDisplay = false;
            state.buttonTypeModalDisplay = false;
            state.scaledPreviewPageMode = false;
        },
        setNodeStateToInsertAfter(state, nodeState) {
            state.nodeStateToInsertAfter = nodeState;
            state.nodeStateToAppendIn = null;
        },
        setNodeStateToAppendIn(state, nodeState) {
            state.nodeStateToAppendIn = nodeState;
            state.nodeStateToInsertAfter = null;
        },
        setEditedAddableSection(state, section) {
            state.addableEditedSection = section;
        },
        setImageLibraryStatus(state, flag) {
            state.imageLibraryActive = flag;
        },
        setVideoLibraryStatus(state, flag) {
            state.videoLibraryActive = flag;
        },
        setImageEditorStatus(state, flag) {
            state.imageEditorActive = flag;
        },
        setImageEditorFile(state, flag) {
            state.imageEditorFile = flag;
        },
        setButtonTypeModalDisplay(state, flag) {
            state.buttonTypeModalDisplay = flag;
        },
        setButtonTypeModalNodeState(state, data) {
            state.buttonTypeModalNodeState = data;
        },
        setInputTypeModalDisplay(state, flag) {
            state.inputTypeModalDisplay = flag;
        },
        setInputTypeModalNodeState(state, data) {
            state.inputTypeModalNodeState = data;
        },
        setScreenTypeModalDisplay(state, flag) {
            state.screenTypeModalDisplay = flag;
        },
        setScreenTypeModalNodeState(state, data) {
            state.screenTypeModalNodeState = data;
        },
        setLottieAnimationModalDisplay(state, flag) {
            state.lottieAnimationModalDisplay = flag
        },
        setLottieAnimationModalNodeState(state, data) {
            state.lottieAnimationModalNodeState = data;
        },
    }
}
